import { useSnackbar } from 'baseui/snackbar';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { MutationBanner } from 'components/Banner';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from 'components/ui/dialog';
import { isMutationSuccess } from 'services/api/base-api';
import {
  useCreateChecklistMutation,
} from 'services/api/checklists/endpoints';
import { useLocale } from 'utils/hooks/useLocale';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
});
type FormFields = z.infer<typeof schema>;

export default function AddChecklist() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [createChecklist, mutation] = useCreateChecklistMutation();

  const { register, handleSubmit, formState } = useForm<FormFields>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '' },
  });

  const close = () => navigate('..', { replace: true });

  const onSubmit = handleSubmit(async (data) => {
    const result = await createChecklist({
      name: data.name,
      description: data.description || null,
    });
    if (isMutationSuccess(result)) {
      snackbar.enqueue({ message: t('Checklist created successfully') });
      close();
    }
  });

  return (
    <Dialog defaultOpen onOpenChange={(open) => !open && close()} size="lg">
      <DialogTitle>{t('Create Checklist')}</DialogTitle>
      <DialogBody>
        <MutationBanner
          message={t('Failed to create checklist')}
          show={mutation.isError}
          focusOnError
        />
        <form id="add-checklist-form" onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('Name')} <span className="text-red-500">*</span>
            </label>
            <input
              {...register('name')}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              placeholder={t('Checklist name')}
            />
            {formState.errors.name && (
              <p className="mt-1 text-sm text-red-600">
                {formState.errors.name.message}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('Description')}
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              placeholder={t('Optional description')}
            />
          </div>
        </form>
      </DialogBody>
      <DialogActions>
        <Button type="button" variant="plain" onPress={close}>
          {t('Cancel')}
        </Button>
        <Button
          type="submit"
          form="add-checklist-form"
          isLoading={mutation.isLoading}
        >
          {t('Create Checklist')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
